import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { onboardingApi, onboardingKeys, type ListingDecision } from "../onboarding/onboardingApi";

const section = css({
  border: "1px solid",
  borderColor: "border.default",
  borderRadius: "md",
  padding: "4",
  marginBottom: "4",
});

/**
 * Partner-listing (référencement) consent — surfaced HERE on /site as its durable management home. The
 * onboarding LISTING_AUTH step only records the FIRST decision (ADR-0013); this lets a manager see the
 * current authorization and grant / revoke it any time — the platform publishing venue data to partner
 * directories (Reserve with Google, …) — right next to the site's own search-engine visibility. Reuses the
 * `/listing-authorization` API + its CAS version. (Before this, the onboarding text promised a settings
 * surface to revoke from that never existed.)
 */
export function ListingAuthPanel({ venueId }: { venueId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const listing = useQuery({
    queryKey: onboardingKeys.listingAuth(venueId),
    queryFn: () => onboardingApi.getListingAuthorization(venueId),
  });

  const decide = useMutation({
    mutationFn: (decision: ListingDecision) =>
      onboardingApi.recordListingDecision(venueId, decision, listing.data?.version ?? 0),
    onSuccess: (next) => {
      qc.setQueryData(onboardingKeys.listingAuth(venueId), next);
    },
  });

  const granted = listing.data?.granted ?? false;
  const decision = listing.data?.decision ?? null;

  return (
    <fieldset className={section} data-testid="site-listing-auth">
      <legend>{t("site.listing.title")}</legend>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("site.listing.description")}</p>

      {listing.isLoading ? (
        <p className={css({ fontSize: "sm", color: "fg.muted", mt: "2" })}>
          {t("site.listing.loading")}
        </p>
      ) : (
        <>
          <p className={css({ fontSize: "sm", mt: "2" })}>
            {t("site.listing.state")}{" "}
            <strong data-testid="site-listing-state">
              {decision ? t(`onboarding.decision.${decision}`) : t("site.listing.undecided")}
            </strong>
          </p>
          {listing.data?.reConfirmRequired && (
            <p className={css({ fontSize: "sm", color: "fg.warning", mt: "1" })}>
              {t("onboarding.listing.reconfirm")}
            </p>
          )}
          {decide.isError && (
            <p role="alert" className={css({ fontSize: "sm", color: "fg.error", mt: "1" })}>
              {t("site.listing.error")}
            </p>
          )}
          <div className={css({ display: "flex", gap: "2", flexWrap: "wrap", mt: "3" })}>
            {granted ? (
              <Button
                size="sm"
                variant="outline"
                loading={decide.isPending}
                data-testid="site-listing-revoke"
                onClick={() => decide.mutate("DECLINED")}
              >
                {t("site.listing.revoke")}
              </Button>
            ) : (
              <Button
                size="sm"
                loading={decide.isPending}
                data-testid="site-listing-authorize"
                onClick={() => decide.mutate("GRANTED")}
              >
                {t("site.listing.authorize")}
              </Button>
            )}
          </div>
        </>
      )}
    </fieldset>
  );
}
